/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { handleResponse } from '@opengeoweb/api';
import { sessionStorageProvider } from '@opengeoweb/shared';
import type {
  CapAlert,
  CapPresets,
  CustomFeature,
} from '../../components/types';
import { addFeedAddressToCustomGeoJSonFeature } from '../addFeedAddressToCustomGeoJsonFeature';

const config = sessionStorageProvider.getConfig();

const capBaseUrl = config.GW_CAP_BASE_URL ?? '';

export interface FeedAddressLink {
  feedAddress: string;
  link: string;
}

export interface CapAlertAndFeatures {
  alert: CapAlert;
  features: CustomFeature[];
}

export const getConfig = async (
  productConfigKey: string,
): Promise<CapPresets> =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  fetch(`assets/${encodeURIComponent(productConfigKey)}`).then(handleResponse);

export const getFeedLinks = async (
  feedAddress: string,
): Promise<FeedAddressLink[]> =>
  fetch(`${capBaseUrl}/feedlinks?url=${encodeURIComponent(feedAddress)}`)
    .then(handleResponse)
    .then((result: string[]) => result.map((link) => ({ link, feedAddress })));

export const getSingleAlert = async (
  alertUrl: FeedAddressLink,
): Promise<CapAlertAndFeatures> =>
  fetch(`${capBaseUrl}/singlealert?url=${encodeURIComponent(alertUrl.link)}`)
    .then(handleResponse)
    .then((result) => {
      const alert = { ...result.alert, feedAddress: alertUrl.feedAddress };
      const features = addFeedAddressToCustomGeoJSonFeature(
        alertUrl.feedAddress,
        result.features.features,
      );
      return { alert, features };
    });
